import FeatherIcon from "feather-icons-react";
import React, { FC, useState } from "react";
import { FormQuestionDto, FormSectionDto } from "@green0meter/api-client";
import { useTranslation } from "react-i18next";

import EmissionFactor from "../EmissionFactor";

import { CircularProgress, IconButton, Stack, Tooltip } from "@components/base";
import { QuestionEditor } from "@components/main/questions";
import { useDeleteFormQuestionConfig, useGetQuestionConfigDetails } from "@api";
import { useMultistepFormStore } from "@components/common/MultistepForm";
import { ConfirmationModal, Drawer } from "@components/common";
import { useUserStore } from "@store/user";

interface EditQuestion {
  question: FormQuestionDto;
  section: FormSectionDto;
}

const EditQuestion: FC<EditQuestion> = ({ question, section }) => {
  const { t } = useTranslation();
  const [deleteQuestionConfirmationOpen, setDeleteQuestionConfirmationOpen] =
    useState(false);
  const [editQuestionOpen, setEditQuestionOpen] = useState(false);

  const {
    formSettings: {
      formBasics: { id },
    },
  } = useMultistepFormStore();

  const { user } = useUserStore();
  const { questionsConfigDetails, isFetchingQuestionsConfigDetails } =
    useGetQuestionConfigDetails(user, question.questionId, editQuestionOpen);
  const { deleteFormQuestionConfig } = useDeleteFormQuestionConfig(user, id);

  const handleEdit = (): void => {
    setEditQuestionOpen(true);
  };

  const handleDelete = (): void => {
    setDeleteQuestionConfirmationOpen(true);
  };

  const deleteQuestion = (): void => {
    setDeleteQuestionConfirmationOpen(false);
    deleteFormQuestionConfig(question.formQuestionId);
  };

  return (
    <>
      <Stack direction="row" spacing={1} mt={2} alignItems="center">
        <EmissionFactor question={question} />
        <Tooltip title={t("multistep.editor.editQuestion")} placement="top">
          <IconButton onClick={handleEdit} color="primary">
            <FeatherIcon icon="edit-3" size={16} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("multistep.editor.removeQuestion")} placement="top">
          <IconButton onClick={handleDelete} color="error">
            <FeatherIcon icon="trash-2" size={16} />
          </IconButton>
        </Tooltip>
      </Stack>
      <Drawer
        open={editQuestionOpen}
        onClose={() => setEditQuestionOpen(false)}
        content={
          isFetchingQuestionsConfigDetails ? (
            <CircularProgress />
          ) : (
            <QuestionEditor
              questionConfig={questionsConfigDetails}
              formSection={section}
              formQuestion={question}
              formId={id}
              onSave={() => setEditQuestionOpen(false)}
              onCancel={() => setEditQuestionOpen(false)}
            />
          )
        }
      />
      <ConfirmationModal
        open={deleteQuestionConfirmationOpen}
        onClose={() => setDeleteQuestionConfirmationOpen(false)}
        onConfirm={deleteQuestion}
        title={t("multistep.editor.removeQuestionConfirmation")}
      />
    </>
  );
};

export default EditQuestion;
